import { faker } from '@faker-js/faker';
import { generateInitialPaginationApi } from '@ingenico/api-sdk-core/mocks';
import type {
  GetAllOsVersionArtifactsByOsVersionIdV2QueryResponseApi,
  GetOsByIdV2QueryResponseApi,
  OsResourceListResponseV2Api,
  OsResourceReadV2Api,
  OsVersionResourceListResponseV2Api,
  OsVersionResourceV2Api,
  PaginationApi,
  SearchFieldDetailApi,
  SearchFieldListResponseApi,
  SearchOsVersionsV2MutationRequestApi,
} from '@ingenico/api-sdk-device-management-private-v2';
import { enumVersionPublicationStatus } from '@ingenico/api-sdk-device-management-private-v2';
import { toNumber } from 'lodash-es';
import type { PathParams, ResponseResolverReturnType, HttpRequestResolverExtras, ResponseResolverInfo } from 'msw';
import { HttpResponse } from 'msw';
import { generateArtifactReadV2Api } from './common';

export async function searchOsVersionCustomHandler(
  context: ResponseResolverInfo<HttpRequestResolverExtras<PathParams>>,
): Promise<ResponseResolverReturnType<OsVersionResourceListResponseV2Api>> {
  const request = (await context.request.json()) as SearchOsVersionsV2MutationRequestApi;

  const url = new URL(context.request.url);

  const offset = toNumber(url.searchParams.get('offset') ?? 0);
  const limit = toNumber(url.searchParams.get('limit') ?? 10);

  const hasFilterById = request.filters?.some(({ name }) => name === 'osId');

  const searchCount = faker.number.int({ min: 1, max: limit });

  const responseData = faker.helpers.multiple(generateMockedSearchOS, { count: searchCount });

  const data: OsVersionResourceListResponseV2Api = hasFilterById
    ? {
        data: [responseData[0]],
        pagination: {
          itemsPerPage: 1,
          startIndex: 0,
          totalResults: 1,
        },
      }
    : {
        data: responseData,
        pagination: generatePaginationApi({ total: 128, offset, limit }),
      };
  return HttpResponse.json(data);
}

function generatePaginationApi({
  total,
  offset,
  limit,
}: {
  readonly total: number;
  readonly offset: number;
  readonly limit: number;
}): PaginationApi {
  const initialTerm = Math.ceil(offset / total);
  return {
    totalResults: total,
    startIndex: initialTerm,
    itemsPerPage: limit,
  };
}

function generateMockedSearchOS(): OsVersionResourceV2Api {
  return {
    artifacts: generateArtifacts(),
    creationDate: faker.date.past(),
    id: faker.string.uuid(),
    longDescription: faker.lorem.paragraph(),
    osId: faker.string.uuid(),
    publisherId: faker.string.uuid(),
    shortDescription: faker.lorem.sentence(),
    status: faker.helpers.arrayElement(Object.values(enumVersionPublicationStatus)),
    versionNumber: '1.1.0',
  };
}

function generateArtifacts(): OsVersionResourceV2Api['artifacts'] {
  const artifactsCount = faker.number.int({ min: 1, max: 4 });

  return Array(artifactsCount)
    .fill(undefined)
    .map((_, index) => ({
      id: faker.string.uuid(),
      order: index,
    })) satisfies OsVersionResourceV2Api['artifacts'];
}

export function getOsSearchFieldsV2Handler(): SearchFieldDetailApi[] {
  const keys = [
    'id',
    'name',
    'longDescription',
    'status',
    'publisherId',
    'providerId',
    'creationDate',
    'type',
    'compatibleDeviceFamilyNames',
    'shortDescription',
    'updatedDate',
  ];
  return keys.map((key) => ({ key: `os.${key}`, type: 'string' }));
}

export function getOsSearchFieldsV2CustomHandlerMock() {
  return HttpResponse.json<SearchFieldListResponseApi>(getOsSearchFieldsV2Handler());
}

export function getAllOsVersionArtifactsByOsVersionIdV2ResponseMock(): GetAllOsVersionArtifactsByOsVersionIdV2QueryResponseApi {
  const count = faker.number.int({ min: 5, max: 20 });

  return {
    data: faker.helpers.multiple(generateArtifactReadV2Api, { count }),
    pagination: generateInitialPaginationApi(count),
  };
}

function generateOsResourceReadV2Api(): OsResourceReadV2Api {
  return {
    id: faker.string.uuid(),
    name: faker.commerce.product(),
    shortDescription: faker.lorem.sentence(),
    longDescription: faker.lorem.paragraph(),
    compatibleDeviceFamilyNames: [faker.helpers.arrayElement(['AXIUM', 'MOVE', 'LINK'])],
    status: faker.helpers.arrayElement(Object.values(enumVersionPublicationStatus)),
    creationDate: faker.date.past(),
    publisherId: faker.string.uuid(),
    osVersionIds: [faker.string.uuid()],
    publishedVersionNumbers: [faker.system.semver()],
    assets: [],
    osVersions: [],
  };
}

export function searchOsV2CustomHandler() {
  const count = faker.number.int({ min: 1, max: 10 });
  return HttpResponse.json<OsResourceListResponseV2Api>({
    pagination: generateInitialPaginationApi(count),
    data: faker.helpers.multiple(generateOsResourceReadV2Api, { count }),
  });
}

export function getOsByIdV2ResponseMock(): GetOsByIdV2QueryResponseApi {
  return generateOsResourceReadV2Api();
}
